import * as ko from "knockout";

import { Feature, FeatureType } from "../map/api";
import { MapViewModel } from "../map/mapViewModel";

import { ErrorLevel, LocationData, LocationType, UnmappableLocation } from "./api";
import { MapProgressIndicator, MapProgressState } from "./mapProgressIndicator";


/**
 * Options to supply when creating a PliMapViewModel.
 */
export interface PliMapOptions {
    /**
     * Called when a feature is added to the map (can be used to create popups).
     */
    mapFeatureCallback?: (f: Feature) => void;
    /**
     * Called when the user clicks the reset button or when the map data is updated.
     */
    mapFocusCallback?: () => void;
    /**
     * The message to display when the map does not contain any locations.
     * @default "No locations to show."
     */
    emptyMessage?: string;
    /**
     * A display name for the plural version of the item types being displayed on the map.
     * @default "Locations"
     */
    pluralEntityType?: string;
    /**
     * A callback to refresh the data from the server and reload via @see loadViewModel.
     */
    refreshData: () => void;
    /**
     * If "true", @see mapFocusCallback is called when new data is loaded for the map.
     */
    focusMapOnLoad?: boolean;
    /**
     * A callback function which transforms a feature's properties into location data.
     */
    getLocationData: (featureProperties: any) => LocationData[];
    /**
     * true if the map controls and MapProgressIndicator should be shown, false otherwise.
     */
    hideControls?: ko.Observable<boolean>;
    /**
     * If "true", the fullscreen button is shown on the map, otherwise the fullscreen button
     * isn't shown on the map.
     */
    showFullscreenButton?: boolean;
    /**
     * Called when the map is toggled between fullscreen and non-fullscreen and vice versa.
     */
    onFullscreenToggle?: (isFullscreen: boolean) => void;
}


/**
 * Data to load into a @see PliMapViewModel.
 */
export interface PliMapRecord {
    /**
     * The data to show on the map of type @see FeatureType.Land.
     */
    landData?: string;
    /**
     * The data to show on the map of type @see FeatureType.Well.
     */
    wellData?: string;
    /**
     * The unmappable locations corresponding to @see landData and @see wellData.
     *
     * Note that map locations which are out of range should not be included as they will
     * be parsed from the provided data.
     */
    unmappableLocations: UnmappableLocation[];
    /**
     * "true" if the server call to get map data is in progress.
     */
    isLoading: boolean;
    /**
     * "true" if the server call to get map data returned a generic error.
     */
    hasGenericError: boolean;
    /**
     * "true" if the map has multiple locations.
     */
    hasMultipleLocations: boolean;
}


/**
 * A view model for displaying a map with a progres indicator.
 */
export class PliMapViewModel {
    /**
     * Creates a new instance of the PliMapViewModel class.
     */
    constructor(options: PliMapOptions) {
        if (!options) { throw new Error("options is required."); }
        if (typeof options.refreshData !== "function") { throw new Error("options.refreshData must be a function."); }
        if (typeof options.getLocationData !== "function") { throw new Error("options.refreshgetLocationDataData must be a function."); }

        this.hideControls = options.hideControls || ko.observable(false);
        this.emptyMessage = options.emptyMessage || "No locations to show.";
        this._getLocationData = options.getLocationData;
        this._landGeoJson = ko.observable(null);
        this._wellGeoJson = ko.observable(null);

        const hideProgressIndicator = ko.observable(false);
        this.map = new MapViewModel({
            onResetFocus: options.mapFocusCallback,
            showFullscreenButton: options.showFullscreenButton,
            onFullscreenToggle: (isFullscreen: boolean): void => {
                // Don't show the progress indicator in fullscreen mode
                hideProgressIndicator(isFullscreen);

                if (options.onFullscreenToggle) {
                    options.onFullscreenToggle(isFullscreen);
                }
            }
        });

        this.isEmpty = ko.pureComputed(() => {
            return !this._landGeoJson()
                && !this._wellGeoJson()
                && !this.progressIndicator.isBusy()
                && this.progressIndicator.state() === MapProgressState.None;
        });

        // Progress indicator
        this.progressIndicator = new MapProgressIndicator({
            pluralEntityType: options.pluralEntityType,
            refreshMap: options.refreshData,
            hide: ko.computed(() => this.hideControls() || hideProgressIndicator())
        });

        const mapFocusCallback = options.focusMapOnLoad ? options.mapFocusCallback : null;
        this._landGeoJson.subscribe((geoJson: GeoJSON.FeatureCollection<GeoJSON.GeometryObject>) =>
            this._updateFeature(geoJson, FeatureType.Land, options.mapFeatureCallback, mapFocusCallback));
        this._wellGeoJson.subscribe((geoJson: GeoJSON.FeatureCollection<GeoJSON.GeometryObject>) =>
            this._updateFeature(geoJson, FeatureType.Well, options.mapFeatureCallback, mapFocusCallback));
    }


    /**
     * Loads data into this PliMapViewModel.
     * @param {PliMapRecord} record The data to load.
     */
    loadViewModel(record: PliMapRecord): void {
        if (record.isLoading) { // Data loading from the server
            this.progressIndicator.loadViewModel({
                hasGenericError: false,
                hasMultipleLocations: false,
                isBusy: true,
                unmappableLocations: []
            });
            return;
        }

        if (record.hasGenericError) { // Call failed
            this.progressIndicator.loadViewModel({
                hasGenericError: true,
                hasMultipleLocations: false,
                isBusy: false,
                unmappableLocations: []
            });
            return;
        }

        let unmappableLocations = record.unmappableLocations;
        let landGeoJson: GeoJSON.FeatureCollection<GeoJSON.GeometryObject> = !record.landData ? null : JSON.parse(record.landData);
        if (!landGeoJson || !landGeoJson.features || !landGeoJson.features.length) {
            landGeoJson = null;
        } else {
            unmappableLocations = unmappableLocations.concat(this._findOutOfRangeLocations(landGeoJson.features));
        }

        let wellGeoJson: GeoJSON.FeatureCollection<GeoJSON.GeometryObject> = !record.wellData ? null : JSON.parse(record.wellData);
        if (!wellGeoJson || !wellGeoJson.features || !wellGeoJson.features.length) {
            wellGeoJson = null;
        } else {
            unmappableLocations = unmappableLocations.concat(this._findOutOfRangeLocations(wellGeoJson.features));
        }

        this._landGeoJson(landGeoJson);
        this._wellGeoJson(wellGeoJson);
        this.progressIndicator.loadViewModel({
            hasGenericError: false,
            isBusy: false,
            hasMultipleLocations: record.hasMultipleLocations,
            unmappableLocations: unmappableLocations
        });
    };


    private _updateFeature(
        geoJson: GeoJSON.FeatureCollection<GeoJSON.GeometryObject>,
        featureType: FeatureType,
        mapFeatureCallback: (f: Feature) => void,
        mapFocusCallback?: () => void)
        : void {

        if (!geoJson) {
            this.map.clearFeatures(featureType);
            return;
        }

        switch (featureType) {
            case FeatureType.Land:
                this.map.setLandFeatures(geoJson, mapFeatureCallback);
                break;
            case FeatureType.Well:
                this.map.setWellFeatures(geoJson, mapFeatureCallback);
                break;
            default:
                break;
        }

        // Set map focus
        if (mapFocusCallback) {
            mapFocusCallback();
        }
    }


    private _findOutOfRangeLocations(features: GeoJSON.Feature<GeoJSON.GeometryObject>[]): UnmappableLocation[] {
        return features
            .filter((feature: GeoJSON.Feature<GeoJSON.GeometryObject>) => {
                // geometry property is falsy if location is outside of supported region
                return feature && !feature.geometry;
            })
            .map((feature: GeoJSON.Feature<GeoJSON.GeometryObject>) => {
                return this._getLocationData(feature.properties).map((locationData: LocationData) => {
                    return {
                        id: locationData.id,
                        errorLevel: ErrorLevel.Warning,
                        message: `The ${LocationType[locationData.type].toUpperCase()} location is valid but is out of range of this map`,
                        titleDisplayName: locationData.titleDisplayName,
                        tooltipDisplayName: locationData.tooltipDisplayName,
                        openItem: locationData.openItem
                    };
                });
            })
            .reduce((result, current) => result.concat(current), []); // flatten array
    }


    /**
     * The map being shown.
     */
    map: MapViewModel;
    /**
     * The MapProgressIndicator for this PliMapViewModel.
     */
    progressIndicator: MapProgressIndicator;
    /**
     * The message to display when the map does not contain any locations.
     * @default "No locations to show."
     */
    emptyMessage: string;
    /**
     * true if the PliMapViewModel contains no locations, false otherwise.
     */
    isEmpty: ko.Computed<boolean>;
    /**
     * true if the map controls and MapProgressIndicator should be shown, false otherwise.
     */
    hideControls: ko.Observable<boolean>;


    private _landGeoJson: ko.Observable<GeoJSON.FeatureCollection<GeoJSON.GeometryObject>>;
    private _wellGeoJson: ko.Observable<GeoJSON.FeatureCollection<GeoJSON.GeometryObject>>;
    private _getLocationData: (featureProperties: any) => LocationData[];
}



// WEBPACK FOOTER //
// pliMapViewModel.ts